/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../../core/components';
import { Component, TemplateRef, ViewChild } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../../core/models';
import { L4RealServiceModel, L7BackendServiceModel, L7RealServiceModel } from '../../../models';
import {
    LanguageService,
    LoadingService,
    LogService,
    ModalService,
    NotifyService,
    RbacService,
    TransactionService,
    UtilsService
} from '../../../../../../core/services';
import { Layer4ApiService, Layer7ApiService } from '../../../http';
import { TableMultiSelectModel } from '../../../../../../core/models/tableMultiSelect.model';
import { catchError, concatMap, mergeMap, takeUntil } from 'rxjs/operators';
import { forkJoin, from, Observable, of, Subscriber } from 'rxjs';

@Component({
    selector: 'app-lb-modals-hanging-realservers',
    styleUrls: ['./hanging-real-servers-modal.component.scss'],
    templateUrl: './hanging-real-servers-modal.component.html',
})

export class HangingRealServersModalComponent extends BaseComponent {
    /**
     * Reference to the content of this component.
     */
    @ViewChild(TemplateRef) public templateRef: TemplateRef<any>;

    /**
     * External: Incoming hangingBackends
     */
    public hangingRealServerIds: string[] = [];

    /**
     * External: Call back function in the parent to update the view on the parent.
     */
    public updateCallback: (...args: any[]) => void;

    /**
     * Store list of backends.
     */
    public backendReferences: any[] = [];

    /**
     * Store list of real servers.
     */
    public realServerReferences: any[] = [];

    /**
     * Store button text and action for select all action dots.
     */
    public multiSelectActions: TableMultiSelectModel[] = [];

    /**
     * Store tables rows.
     */
    public tableRows: any = [];

    /**
     * To store the progress message.
     */
    public progressMessage: string = '';

    /**
     * Store permissions.
     */
    public permissionsLookup: any = {};

    /**
     * Store the dropdown backend id references.
     */
    public dropdownReferences: any = [];

    /**
     * Store of multiple selected rows in the table.
     * @private
     */
    private _selectedRows: any[] = [];

    constructor(
        private languageService: LanguageService,
        private layer4ApiService: Layer4ApiService,
        private layer7ApiService: Layer7ApiService,
        private loadingService: LoadingService,
        private logService: LogService,
        private modalService: ModalService,
        private notifyService: NotifyService,
        private rbacService: RbacService,
        private transactionService: TransactionService,
        private utilsService: UtilsService
    ) {
        super();

        this.setUpPageData();
        this.registerButtons();
        this.setSelectAllActions();
    }

    set selectedRows(rows: any[]) {
        this._selectedRows = [...rows];
    }

    get selectedRows() {
        return this._selectedRows;
    }

    /**
     * Triggered when a table row is selected.
     * @param selected
     */
    public onSelect(selected: any): void {
        this.selectedRows = [...selected];
    }

    /**
     * Set backend id.
     * @param backendId
     * @param row
     * @param index
     */
    public setBackendId(backendId: string, row: any, index: number): void {
        this.dropdownReferences[index] = backendId;
        const rowIndex: any = this.selectedRows?.findIndex((item: any): boolean => item.id === row.id);
        const rows = this.selectedRows;

        if (backendId === null && rowIndex !== -1) {
            rows.splice(rowIndex, 1);
        } else if (backendId !== null && rowIndex === -1) {
            rows.push(row);
        } else {
            this.logService.debug('HangingRealServersModalComponent:setBackendId', 'Issue matching backendId or rowIndex');
        }

        this.selectedRows = rows;

        this.setSelectAllActions();
    }

    /**
     * Confirm assign to backend.
     * @param rows
     * @param index
     */
    public confirmAssignToBackend(rows: any[] = this.selectedRows, index?: number): void {
        let backend: any = {};
        if (rows.length === 1 && index > -1) {
            const backendId: any = this.dropdownReferences[index];
            backend = this.backendReferences.find(item => item.id === backendId);
        }

        const message: string = rows.length === 1 && index > -1
            // eslint-disable-next-line max-len
            ? `${this.languageService.translate('hanging_real_assign_confirm_message_single')} ${rows[0].name} ${this.languageService.translate('to')} ${backend?.name}?`
            : this.languageService.translate('hanging_real_assign_confirm_message_multiple');

        const confirmationObject = {
            title: 'hanging_real_assign',
            message: message,
            options: { translateText: false },
            callBack: this.assignRealServersTransaction.bind(this, rows)
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Delete transaction.
     * @param realServers
     * @private
     */
    private assignRealServersTransaction(realServers: L4RealServiceModel[] | L7RealServiceModel[]): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.notifyService.muteLocalNotifications();
            this.transactionService.startTransaction()
                .pipe(
                    concatMap(() => this.updateBackend(realServers)),
                    concatMap(() => this.commitTransaction()),
                    takeUntil(this.destroyed$)
                )
                .subscribe({
                    next: (): void => {
                        this.notifyService.unmuteLocalNotifications();
                        this.loadingService.loadingActionDotsEmitter.next(false);
                        this.progressMessage = '';
                        this.modalService.close('realServerProgressModal');
                        this.notifyService.success('hanging_real_assigned_all', { messageTranslate: true });
                        this.updateView();

                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error(
                            'HangingRealServerModalComponent:assignRealServersTransaction',
                            'Error with the transaction',
                            error
                        );
                        this.transactionService.rollbackTransaction();
                        observer.error(error);
                    }
                });
        });
    }

    /**
     * Delete the real server(s).
     * @param realServers
     * @private
     */
    private updateBackend(realServers: any[]): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const formattedData: any[] = this.getFormattedData(realServers, this.dropdownReferences, this.backendReferences);
            const dataToSubmit = from(formattedData || []);
            const translation: string = this.languageService.translate('hanging_real_deleting');
            const ofText: string = this.languageService.translate('of');
            let index: number = 1;
            if (formattedData.length > 1) {
                this.modalService.openModalInParent('realServerProgressModal');
                this.progressMessage = `${translation} ${index} ${ofText} ${formattedData.length}`;
            }

            const call: Observable<any> = dataToSubmit.pipe(
                mergeMap((data: any) => this.assignedHangingRealServerHelper(data).pipe(catchError(error => of(error))))
            );
            call.subscribe({
                next: (): void => {
                    if (formattedData.length > 1) {
                        this.progressMessage = `${translation} ${index} ${ofText} ${formattedData.length}`;
                    }
                    index++;
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                },
                complete: (): void => {
                    observer.next();
                    observer.complete();
                }
            });
        });
    }

    /**
     * Format real servers before submission.
     * @param realServers
     * @param dropdownReferences
     * @param backendReferences
     * @private
     */
    private getFormattedData(realServers: any[], dropdownReferences: string[], backendReferences: any[]): any[] {
        const formattedData: any[] = [];

        for (const realServer of realServers) {
            const realServerIndex: number = this.tableRows.findIndex((item: {
                id: any;
            }): boolean => item.id === realServer.id);
            const backendId: string = dropdownReferences[realServerIndex];
            if (backendId) {
                const lookupIndex: number = formattedData.findIndex(
                    (item: any): boolean => item.id === backendId && item.templateType === realServer.templateType
                );

                if (lookupIndex !== -1) {
                    formattedData[lookupIndex].realServersIds.push(realServer.id);
                } else {
                    formattedData.push({
                        id: backendId,
                        templateType: realServer.templateType,
                        realServersIds: this.getAllRealServers(realServer, backendReferences, backendId),
                    });
                }
            }
        }

        return formattedData;
    }

    /**
     * Get existing real server ids from the selected backend and concat them in one array.
     * @param realServer
     * @param backendReferences
     * @param backendId
     * @private
     */
    private getAllRealServers(realServer: any, backendReferences: any[], backendId: string): string[] {
        const backend: any = backendReferences.find((item: any): boolean => item.id === backendId);

        return backend?.ripIds ? [realServer.id].concat(backend.ripIds) : [realServer.id];
    }

    /**
     * Assign hanging real servers helper
     * @param data
     * @private
     */
    private assignedHangingRealServerHelper(data: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const apiService: any = data.templateType === 'layer4' ? this.layer4ApiService : this.layer7ApiService;
            const realServerIds = { ripIds: data.realServersIds };

            apiService.updateBackend(data.id, realServerIds)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (response: any): void => {
                        this.updateTableData(data.realServersIds);
                        observer.next(response);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Updated table data.
     * @param realServerIds
     * @private
     */
    private updateTableData(realServerIds: string[]): void {
        for (const realServerId of realServerIds) {
            const deleteIndex: number = this.hangingRealServerIds.findIndex((item: any): boolean => item === realServerId);
            if (deleteIndex !== -1) {
                this.hangingRealServerIds.splice(deleteIndex, 1);
            }
        }
    }

    /**
     * Confirm delete of real server(s).
     * @param realservers
     */
    public confirmDeleteRealServers(realservers: any[] = this.selectedRows): void {
        const message: string = realservers.length > 1
            ? this.languageService.translate('hanging_real_delete_confirm_message_multiple')
            : this.languageService.translate('hanging_real_delete_confirm_message_single') + ` ${realservers[0].name}?`;

        const confirmationObject = {
            title: 'hanging_real_delete',
            message: message,
            options: { translateText: false },
            callBack: this.deleteRealServersTransaction.bind(this, realservers)
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Delete transaction.
     * @param realServers
     * @private
     */
    private deleteRealServersTransaction(realServers: L4RealServiceModel[] | L7BackendServiceModel[]): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.notifyService.muteLocalNotifications();
            this.transactionService.startTransaction()
                .pipe(
                    concatMap(() => this.deleteRealServer(realServers)),
                    concatMap(() => this.commitTransaction()),
                    takeUntil(this.destroyed$)
                )
                .subscribe({
                    next: (): void => {
                        this.notifyService.unmuteLocalNotifications();
                        this.loadingService.loadingActionDotsEmitter.next(false);
                        this.progressMessage = '';
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error(
                            'HangingRealServerModalComponent:deleteRealServersTransaction',
                            'Error with the transaction',
                            error
                        );
                        this.transactionService.rollbackTransaction();
                        observer.error(error);
                    },
                    complete: (): void => {
                        this.modalService.close('realServerProgressModal');
                        this.notifyService.success('hanging_reals_deleted_all', { messageTranslate: true });
                        this.updateView();
                        observer.next();
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Delete the real server(s).
     * @param realServers
     * @private
     */
    private deleteRealServer(realServers: any[]): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.notifyService.muteLocalNotifications();
            const dataToSubmit = from(realServers || []);
            const translation: string = this.languageService.translate('hanging_real_deleting');
            const ofText: string = this.languageService.translate('of');
            let index: number = 1;
            if (realServers.length > 1) {
                this.modalService.openModalInParent('realServerProgressModal');
                this.progressMessage = `${translation} ${index} ${ofText} ${realServers.length}`;
            }
            const call: Observable<any> = dataToSubmit.pipe(
                mergeMap((realServer: any) => this.deleteHangingRealServerHelper(realServer).pipe(catchError(error => of(error))))
            );
            call.subscribe({
                next: (): void => {
                    if (realServers.length > 1) {
                        this.progressMessage = `${translation} ${index} ${ofText} ${realServers.length}`;
                    }
                    index++;
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                },
                complete: (): void => {
                    observer.next();
                    observer.complete();
                }
            });
        });
    }

    /**
     * Helper function for deleting.
     * @private
     * @param realServer
     */
    private deleteHangingRealServerHelper(realServer: any) {
        return new Observable((observer) => {
            const apiService: any = realServer.templateType === 'layer4' ? this.layer4ApiService : this.layer7ApiService;
            apiService.deleteRealServer(realServer.id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (response: any): void => {
                        this.updateTableData([realServer.id]);
                        observer.next(response);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Commit transaction.
     * @private
     */
    private commitTransaction(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.transactionService.commitTransaction()
                .subscribe({
                    next: (): void => {
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Helper function for updating the view.
     * @private
     */
    private updateView(): void {
        if (this.hangingRealServerIds.length) {
            // Call callback on the parent component.
            this.updateCallback();
            // Reset the selectedRows variable when all selected rows have been deleted.
            this.selectedRows = [];
            // Updated page and get a new list of backends.
            this.setUpPageData();
        } else {
            this.updateCallback();
            this.modalService.close('hangingRealsModal');
        }
    }

    /**
     * Set up page data.
     * @private
     */
    private setUpPageData(): void {
        const apiCalls = {
            'backends4': this.layer4ApiService.getBackends(),
            'reals4': this.layer4ApiService.getRealServers(),
            'backends7': this.layer7ApiService.getBackends(),
            'reals7': this.layer7ApiService.getRealServers()
        };
        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    if (data['backends4']) {
                        this.utilsService.addTypeToService(data['backends4'], 'layer4', 'Layer 4');
                    }

                    if (data['reals4']) {
                        this.utilsService.addTypeToService(data['reals4'], 'layer4', 'Layer 4');
                    }

                    if (data['backends7']) {
                        this.utilsService.addTypeToService(data['backends7'], 'layer7', 'Layer 7');
                    }

                    if (data['reals7']) {
                        this.utilsService.addTypeToService(data['reals7'], 'layer7', 'Layer 7');
                    }

                    this.backendReferences = data['backends4'].concat(data['backends7']);
                    this.realServerReferences = data['reals4'].concat(data['reals7']);

                    this.populateRows();
                    if (!this.modalService.isModelOpen('hangingRealsModal')) {
                        this.modalService.openModalInParent('hangingRealsModal');
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('HangingBackendsModalComponent: setUpPageData', error.message);
                }
            });
    }

    /**
     * Populate the hanging backends array.
     * @private
     */
    private populateRows(): void {
        this.tableRows = [];

        for (const hangingReal of this.hangingRealServerIds) {
            const real: any = this.realServerReferences.find((item: any): boolean => item.id === hangingReal);
            const backends: any[] = this.backendReferences.filter((item: any): boolean => item.templateType === real.templateType);
            const dropdownBackends: DropdownModel[] = this.utilsService.convertArrayToDropDown(backends, 'id', 'name');
            if (backends.length) {
                dropdownBackends.unshift(<DropdownModel>{ key: null, value: '- Select -' });
            }

            this.tableRows.push({
                id: real.id,
                name: real.name,
                ip: real.ip,
                port: real.port,
                templateType: real.templateType,
                templateTypeFriendlyName: real.templateTypeFriendlyName,
                backends: dropdownBackends
            });

            this.dropdownReferences.push(null);
        }
    }

    /**
     * Register button permissions.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'assign-layer-4': [{ path: '/ipvsDirector/backends/:id', methods: ['PUT'] }],
            'assign-layer-7': [{ path: '/haproxy/backends/:id', methods: ['PUT'] }],
            'delete-real-layer-4': [{ path: '/ipvsDirector/rips/:id', methods: ['DELETE'] }],
            'delete-real-layer-7': [{ path: '/haproxy/rips/:id', methods: ['DELETE'] }]

        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
        this.permissionsLookup['hanging-reals-assign-to-backend'] = this.checkAtLeastOne('assign-layer-4', 'assign-layer-7');
        this.permissionsLookup['hanging-reals-delete-backend'] = this.checkAtLeastOne('delete-real-layer-4', 'delete-real-layer-7');
    }

    /**
     * Check at least one is set.
     * @param layer4
     * @param layer7
     * @private
     */
    private checkAtLeastOne(layer4: string, layer7: string): boolean {
        return this.permissionsLookup[layer4] || this.permissionsLookup[layer7];
    }

    /**
     * Set the select all button.
     * @private
     */
    private setSelectAllActions(): void {
        this.multiSelectActions = [
            {
                buttonText: 'hanging_real_assign_backend',
                buttonAction: this.confirmAssignToBackend.bind(this),
                buttonDisabled: !this.permissionsLookup['hanging-reals-assign-to-backend'] || this.assignArrayIsEmpty()
            },
            {
                buttonText: 'hanging_real_delete_all',
                buttonAction: this.confirmDeleteRealServers.bind(this),
                buttonDisabled: !this.permissionsLookup['hanging-reals-delete-backend']
            }
        ];
    }

    /**
     * If the dropdown reference is empty the assign button should be disabled.
     * @private
     */
    private assignArrayIsEmpty(): boolean {
        const clonedArray: any[] = this.utilsService.duplicateObject(this.dropdownReferences);

        return !this.utilsService.cleanArray(clonedArray).length;
    }

}
